/**
 * MessageBubble (06 §4.3) — incoming/outgoing with SVG tail on run-starts,
 * in-bubble time+tick row with a trailing spacer so text never collides with
 * the meta (WhatsApp's signature behavior), reply context, group sender name,
 * media slot, deleted variant, and a context menu (right-click / hover chevron).
 */
import { useState } from 'react';
import { ChevronDown, Ban, FileText, Play } from 'lucide-react';
import type { Conversation, Message } from '../../data/model';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { clockTime, colorForId, isEmojiJumbo } from '../../lib/format';
import { TickIcon } from './TickIcon';
import { QuotedPreview } from './QuotedPreview';
import { MessageContextMenu } from './MessageContextMenu';
import styles from './MessageBubble.module.css';

interface Props {
  message: Message;
  conv: Conversation;
  /** First bubble of a consecutive same-sender run → render tail. */
  runStart: boolean;
  /** Resolve a reply-to seq to its message. */
  resolveReply: (seq: number) => Message | undefined;
}

function BubbleTail({ outgoing }: { outgoing: boolean }) {
  // Small notch on the top-outer corner.
  return (
    <span className={`${styles.tail} ${outgoing ? styles.tailOut : styles.tailIn}`} aria-hidden="true">
      <svg viewBox="0 0 8 13" width="8" height="13">
        <path
          d={outgoing ? 'M0 0H8L0 13V0Z' : 'M8 0H0L8 13V0Z'}
          fill="currentColor"
        />
      </svg>
    </span>
  );
}

export function MessageBubble({ message, conv, runStart, resolveReply }: Props) {
  const me = useChatStore((s) => s.me);
  const userById = useChatStore((s) => s.userById);
  const openMedia = useUiStore((s) => s.openMedia);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);

  const outgoing = message.senderId === me.id;
  const sender = userById(message.senderId);
  const showSenderName = conv.type === 'group' && !outgoing && runStart;
  const reply = message.replyToSeq != null ? resolveReply(message.replyToSeq) : undefined;
  const jumbo = !message.media && isEmojiJumbo(message.text);

  const openMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenu({ x: Math.min(e.clientX, window.innerWidth - 220), y: Math.min(e.clientY, window.innerHeight - 220) });
  };

  return (
    <li
      className={`${styles.line} ${outgoing ? styles.out : styles.in} ${runStart ? styles.runStart : styles.runCont}`}
      data-dir={outgoing ? 'out' : 'in'}
      onContextMenu={openMenu}
    >
      <div className={styles.bubble}>
        {runStart && <BubbleTail outgoing={outgoing} />}

        {!message.deleted && (
          <button className={styles.chevron} onClick={openMenu} aria-label="Message options">
            <ChevronDown size={18} />
          </button>
        )}

        {message.forwarded && !message.deleted && (
          <span className={styles.forwarded}>↪ Forwarded</span>
        )}

        {showSenderName && sender && (
          <span className={styles.senderName} style={{ color: colorForId(sender.id) }}>
            {sender.displayName}
          </span>
        )}

        {reply && sender && (
          <QuotedPreview
            senderId={reply.senderId}
            senderName={
              reply.senderId === me.id ? 'You' : userById(reply.senderId)?.displayName ?? 'Unknown'
            }
            message={reply}
          />
        )}

        {message.deleted ? (
          <span className={styles.deleted}>
            <Ban size={15} aria-hidden="true" />
            {outgoing ? 'You deleted this message' : 'This message was deleted'}
          </span>
        ) : (
          <>
            {message.media && (
              <MediaSlot message={message} onOpen={() => openMedia(message.clientMsgId)} />
            )}
            {message.text && (
              <span className={`${styles.text} ${jumbo ? styles.jumbo : ''}`}>
                {message.text}
                {/* zero-width spacer reserving room for the meta row (06 §4.3) */}
                <span className={styles.metaSpacer} aria-hidden="true">
                  {clockTime(message.sentAtMs)}
                </span>
              </span>
            )}
            <span className={styles.meta}>
              <time className={styles.time} dateTime={new Date(message.sentAtMs).toISOString()}>
                {clockTime(message.sentAtMs)}
              </time>
              {outgoing && <TickIcon status={message.status} />}
            </span>
          </>
        )}
      </div>

      {menu && (
        <MessageContextMenu message={message} x={menu.x} y={menu.y} onClose={() => setMenu(null)} />
      )}
    </li>
  );
}

function MediaSlot({ message, onOpen }: { message: Message; onOpen: () => void }) {
  const m = message.media!;
  if (message.contentType === 'image') {
    return (
      <button className={styles.mediaImg} onClick={onOpen} aria-label="Open photo">
        <img src={m.url} alt="" loading="lazy" />
      </button>
    );
  }
  if (message.contentType === 'audio') {
    return (
      <div className={styles.voice}>
        <button className={styles.voiceBtn} aria-label="Play voice message">
          <Play size={18} />
        </button>
        <span className={styles.waveform} aria-hidden="true">
          {Array.from({ length: 28 }).map((_, i) => (
            <span key={i} style={{ height: `${6 + ((i * 7) % 18)}px` }} />
          ))}
        </span>
        <span className={styles.duration}>{Math.round(m.durationSec ?? 0)}s</span>
      </div>
    );
  }
  return (
    <button className={styles.doc} onClick={onOpen}>
      <FileText size={22} />
      <span className={styles.docMeta}>
        <span className={styles.docName}>{m.fileName ?? 'Document'}</span>
        <span className={styles.docSize}>{Math.round(m.size / 1024)} KB</span>
      </span>
    </button>
  );
}
